/* eslint-disable react/prop-types */
import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Box,
  FormControl,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  Card,
  Typography,
  Tooltip,
  Paper,
  Chip,
  CircularProgress,
  IconButton,
  Grid
} from "@mui/material";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import CalendarRoot from "examples/Calendar/CalendarRoot";
import dayjs from "dayjs";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import { styled } from "@mui/material/styles";
import EventIcon from "@mui/icons-material/Event";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import TodayIcon from "@mui/icons-material/Today";
import { attendenceMonthlySummary } from "api/attendance";
import { getAttendenceStatus } from "../api/attendance";
import FlagLoader from "layouts/flagloader";

// Half day background style
const HalfDayBackground = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'linear-gradient(90deg, #4CAF50 50%, #F44336 50%)',
  opacity: 0.15,
  pointerEvents: 'none',
  borderRadius: '4px',
}));

const formatDateLocal = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const AttendanceCalendar = ({
  onDateRangeSelect,
  selectedMonth: externalSelectedMonth,
  selectedYear: externalSelectedYear,
  onMonthChange,
  onYearChange,
  userId, // 👈 ADD THIS PROP to get user ID when viewing team member
}) => {
  const calendarRef = useRef(null);
  const [selectedMonth, setSelectedMonth] = useState(
    externalSelectedMonth !== undefined ? externalSelectedMonth : new Date().getMonth()
  );
  const [selectedYear, setSelectedYear] = useState(
    externalSelectedYear !== undefined ? externalSelectedYear : new Date().getFullYear()
  );
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Colors based on your response structure
  const statusColors = {
    full: {
      bg: "#4CAF50",
      bgLight: "#E8F5E9",
      text: "#FFFFFF",
      label: "Full",
      type: "full"
    },
    absent: {
      bg: "#F44336",
      bgLight: "#FFEBEE",
      text: "#FFFFFF",
      label: "Absent",
      type: "absent"
    },
    holiday: {
      bg: "#FF9800",
      bgLight: "#FFF3E0",
      text: "#FFFFFF",
      label: "Holiday",
      type: "holiday"
    },
    weekend_off: {
      bg: "#F97316", // Orange
      bgLight: "#FFEDD5",
      text: "#FFFFFF",
      label: "Weekend",
      type: "weekend"
    },
    half: {
      bg: "linear-gradient(90deg, #4CAF50 50%, #F44336 50%)",
      bgLight: "linear-gradient(90deg, #E8F5E9 50%, #FFEBEE 50%)",
      text: "#525253",
      label: "Half Day",
      type: "half_day"
    },
    leave: {
      bg: "#8B5CF6", // Purple
      bgLight: "#F3E5F5",
      text: "#FFFFFF",
      label: "Leave",
      type: "leave"
    },
    no_reason_declared: {
      bg: "#757575",
      bgLight: "#F5F5F5",
      text: "#FFFFFF",
      label: "No Reason",
      type: "no_reason_declared"
    },
  };

  // Create a Set of dates that have events for quick lookup
  const eventDateSet = useMemo(() => {
    return new Set(calendarEvents.map(e => e.date));
  }, [calendarEvents]);

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Fetch attendance data — send same payload format as table view (from/to/UserId)
  const fetchAttendanceData = async (month, year) => {
    setLoading(true);
    try {
      // compute start/end of month in YYYY-MM-DD (same as Table)
      const start = dayjs().year(year).month(month).startOf("month").format("YYYY-MM-DD");
      const end = dayjs().year(year).month(month).endOf("month").format("YYYY-MM-DD");

      const params = {
        from: start,
        to: end,
      };

      if (userId) {
        params.UserId = userId;
      }

      const response = await getAttendenceStatus(params);
      if (response?.timeline) {
        const events = response.timeline.map((item) => {
          const statusKey = item.status?.toLowerCase() || "";
          const colorConfig = statusColors[statusKey] || {};

          return {
            id: item.date,
            title: colorConfig.label,
            date: item.date,
            backgroundColor: colorConfig.bg,
            borderColor: colorConfig.bg,
            textColor: colorConfig.text,
            allDay: true,
            display: "block",
            extendedProps: {
              status: item.status,
              reason: item.status,
              date: item.date,
              originalData: item,
              statusKey: statusKey,
              bgColor: colorConfig.bg,
              bgLight: colorConfig.bgLight,
            },
          };
        });

        setCalendarEvents(events);
        showSnackbar(`Loaded ${events.length} attendance records`, "success");
      } else {
        setCalendarEvents([]);
        showSnackbar("No attendance data found", "warning");
      }
    } catch (error) {
      console.error("Error fetching attendance data:", error);
      setCalendarEvents([]);
      showSnackbar("Error loading attendance data", "error");
    } finally {
      setLoading(false);
    }
  };

  const getMonthName = (monthIndex) => {
    const months = [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December"
    ];
    return months[monthIndex];
  };

  // Sync with external props
  useEffect(() => {
    if (externalSelectedMonth !== undefined) {
      setSelectedMonth(externalSelectedMonth);
    }
    if (externalSelectedYear !== undefined) {
      setSelectedYear(externalSelectedYear);
    }
  }, [externalSelectedMonth, externalSelectedYear]);

  // Fetch data when month/year changes OR userId changes
  useEffect(() => {
    fetchAttendanceData(selectedMonth, selectedYear);
  }, [selectedMonth, selectedYear, userId]); // 👈 Add userId to dependency array

  // Navigate calendar to selected month/year
  useEffect(() => {
    if (calendarRef.current && !loading) {
      const calendarApi = calendarRef.current.getApi();
      const newDate = new Date(selectedYear, selectedMonth, 1);
      calendarApi.gotoDate(newDate);
    }
  }, [selectedMonth, selectedYear, loading]);

  // Handle month change
  const handleMonthChange = (event) => {
    const newMonth = parseInt(event.target.value);
    setLoading(true);
    setSelectedMonth(newMonth);
    if (onMonthChange) {
      onMonthChange(newMonth);
    }
  };

  // Handle year change
  const handleYearChange = (event) => {
    const newYear = parseInt(event.target.value);
    setLoading(true);
    setSelectedYear(newYear);
    if (onYearChange) {
      onYearChange(newYear);
    }
  };

  // Custom day cell content renderer
  const dayCellContent = (cellInfo) => {
    const dateStr = formatDateLocal(cellInfo.date);

    // Use the eventDateSet to check if date has an event
    const hasEvent = eventDateSet.has(dateStr);

    if (hasEvent) {
      const event = calendarEvents.find(e => e.date === dateStr);

      if (event) {
        const isHalfDay = ['half'].includes(event.extendedProps.statusKey);

        return (
          <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
            {/* Full day background for non-half days */}
            {!isHalfDay && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: event.extendedProps.bgLight || event.backgroundColor,
                  opacity: 0.3,
                  borderRadius: '4px',
                }}
              />
            )}

            {/* Half day background */}
            {isHalfDay && (
              <HalfDayBackground />
            )}

            {/* Date number */}
            <Box
              sx={{
                position: 'relative',
                zIndex: 1,
                textAlign: 'center',
                fontWeight: 'bold',
                fontSize: '0.9rem',
                padding: '6px 6px 2px 6px',
                color: cellInfo.isToday ? '#1976d2' : '#424242',
              }}
            >
              {cellInfo.dayNumberText}
            </Box>

            {/* Event badge */}
            <Tooltip
              title={event?.extendedProps?.originalData?.status_reason}
              arrow
              placement="top"
            >
              <Box
                sx={{
                  position: 'relative',
                  zIndex: 2,
                  marginTop: '2px',
                  padding: '2px 4px',
                  borderRadius: '4px',
                  backgroundColor: event.backgroundColor,
                  color: event.textColor,
                  fontSize: '0.7rem',
                  fontWeight: 'bold',
                  textAlign: 'center',
                  mx: 0.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: '20px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  cursor: 'default',
                  '&:hover': {
                    opacity: 0.9,
                    transform: 'scale(1.05)',
                  },
                }}
              >
                {event?.title}
              </Box>
            </Tooltip>
          </Box>
        );
      }
    }

    // Return default view for days without events
    return (
      <Box sx={{ textAlign: "center", padding: "6px" }}>
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: "0.9rem",
            color: cellInfo.isToday ? '#1976d2' : "#9e9e9e",
          }}
        >
          {cellInfo.dayNumberText}
        </Typography>
      </Box>
    );
  };

  // Custom event render function
  const renderEventContent = () => {
    return null; // We're handling events in dayCellContent
  };

  // Calculate attendance statistics
  const calculateStats = () => {
    const stats = {
      full: 0,
      absent: 0,
      holiday: 0,
      weekend_off: 0,
      half: 0,
      leave: 0,
      no_reason_declared: 0,
      total: calendarEvents.length
    };

    calendarEvents.forEach(event => {
      const status = event.extendedProps.statusKey;
      if (status && stats.hasOwnProperty(status)) {
        stats[status]++;
      }
    });

    return stats;
  };


  const stats = calculateStats();

  const handleDateSelect = (selectInfo) => {
    const { start, end } = selectInfo;

    if (!start || isNaN(new Date(start).getTime())) {
      console.warn("Invalid start date", start);
      return;
    }

    const startDate = new Date(start);
    const endDate =
      end && !isNaN(new Date(end).getTime())
        ? new Date(end)
        : new Date(start);

    if (onDateRangeSelect) {
      onDateRangeSelect({
        start: startDate.toISOString().split("T")[0],
        end: endDate.toISOString().split("T")[0],
      });
    }
  };

  return (
    <SoftBox>
      {/* Calendar Controls */}
      <Card sx={{ p: 2, mb: 1, borderRadius: 2, mt: -1.5 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={-2}>
          <Box display="flex" gap={1} alignItems="center">
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <Select
                value={selectedMonth}
                onChange={handleMonthChange}
                variant="outlined"
                disabled={loading}
                sx={{
                  backgroundColor: 'background.paper',
                  fontWeight: 600,
                  fontSize: '0.85rem',
                  borderRadius: 1.5,
                  '& .MuiSelect-select': {
                    py: 1
                  }
                }}
              >
                {[
                  "January", "February", "March", "April", "May", "June",
                  "July", "August", "September", "October", "November", "December"
                ].map((month, index) => (
                  <MenuItem
                    key={month}
                    value={index}
                    sx={{
                      fontSize: '0.85rem',
                      fontWeight: selectedMonth === index ? 600 : 400
                    }}
                  >
                    {month}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 90 }}>
              <Select
                value={selectedYear}
                onChange={handleYearChange}
                variant="outlined"
                disabled={loading}
                sx={{
                  backgroundColor: 'background.paper',
                  fontWeight: 600,
                  fontSize: '0.85rem',
                  borderRadius: 1.5,
                  '& .MuiSelect-select': {
                    py: 1
                  }
                }}
              >
                {Array.from({ length: 11 }, (_, i) => {
                  const y = new Date().getFullYear() - 5 + i;
                  return (
                    <MenuItem
                      key={y}
                      value={y}
                      sx={{
                        fontSize: '0.85rem',
                        fontWeight: selectedYear === y ? 600 : 400
                      }}
                    >
                      {y}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Box>

          <Box display="flex" alignItems="center" gap={1}>
            <Chip
              label={`${stats.total} records`}
              color="info"
              variant="outlined"
              size="small"
            />
            {loading && (
              <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
                <CircularProgress size={18} />
              </Box>
            )}
          </Box>
        </Box>

        {/* Loading indicator */}
        {loading && (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            py={3}
          >
            {/* <CircularProgress
                  size={35}
                  sx={{
                    color: 'primary.main',
                    mb: 1.5
                  }}
                /> */}
            <FlagLoader />
            {/* <Typography variant="body2" color="text.primary">
              {userId ? "Loading team member attendance..." : "Loading attendance data..."}
            </Typography> */}
          </Box>
        )}

        {/* Calendar */}
        {!loading && (
          <CalendarRoot>
            <FullCalendar
              ref={calendarRef}
              plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
              initialView="dayGridMonth"
              editable={false}
              selectable={true}
              // select={handleDateSelect}
              events={calendarEvents}
              eventContent={renderEventContent}
              dayCellContent={dayCellContent}
              height="500px"
              headerToolbar="false"
            />
          </CalendarRoot>
        )}
      </Card>

      {/* Attendance Summary */}
      <Card sx={{ p: 2, borderRadius: 2 }}>
        <SoftTypography variant="h6" gutterBottom display="flex" alignItems="center">
          Attendance Summary ({getMonthName(selectedMonth)} {selectedYear})
        </SoftTypography>

        <Grid container spacing={1.5} mb={2}>
          {Object.entries(statusColors).map(([key, config]) => (
            <Grid item xs={6} sm={4} md={3} lg={2.4} key={key}>
              <Box sx={{
                p: 1.5,
                borderRadius: 1.5,
                backgroundColor: config.bgLight,
                borderLeft: `3px solid ${config.bg}`,
                height: '100%'
              }}>
                <SoftTypography variant="h6" color="text.primary" sx={{ mb: 0.5 }}>
                  {stats[key] || 0}
                </SoftTypography>
                <SoftTypography variant="h6" color="text.secondary" >
                  {config.label}
                </SoftTypography>
              </Box>
            </Grid>
          ))}
        </Grid>
        <SoftTypography variant="h6" gutterBottom>
          Status Legend
        </SoftTypography>
        <Box display="flex" flexWrap="wrap" gap={1.5}>
          {Object.entries(statusColors).map(([key, config]) => (
            <Box
              key={key}
              display="flex"
              alignItems="center"
              gap={0.75}
              sx={{
                p: 1,
                borderRadius: 1,
                backgroundColor: key.includes('half') ? 'transparent' : config.bgLight,
                background: key.includes('half') ? config.bgLight : 'none',
                border: `1px solid ${config.text === '#FFFFFF' ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.3)'}`
              }}
            >
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  background: config.bg,
                  borderRadius: '3px',
                  border: `1px solid ${config.text === '#FFFFFF' ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.3)'}`
                }}
              />
              <SoftTypography variant="h6" color="text.primary">
                {config.label}
              </SoftTypography>
            </Box>
          ))}
        </Box>
      </Card>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%", fontSize: '0.85rem' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </SoftBox>
  );
};

export default AttendanceCalendar;
